import cx from 'classnames';
import debounce from 'lodash/debounce';
import React, { useState, useRef, useCallback, useMemo, memo, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { playButtonClickSound, playInputSound } from '@wg/web2clientapi/sound';

import settings from '~/settings';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useClickOutside from '~/hooks/useClickOutside';
import useMount from '~/hooks/useMount';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { autocompleteActions } from '~/store/slices/autocompleteSlice';
import { fetchAutompleteResultThunk } from '~/store/thunks/autocompleteThunks';
import { fetchSearchResultThunk } from '~/store/thunks/searchThunks';

import { actionsSearch } from '~/Actions/ActionSearch';

import { ClanSearchAutocomplete } from '~/UIKit/components';

import styles from './ClanSearchInput.scss';

import type { RootState } from '~/store';
import type { IClanData } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

const DEBOUNCE_INTERVAL = 300;

const stateSelector = (state: RootState) => {
  return {
    autocomplete: state.autocomplete,
    term: state.searchResult?.term,
  };
};

interface IClanSearchInput {
  searchType?: string;
  showIcon?: boolean;
  onSubmit?: () => void;
  onHistoryItemClick: (clan: IClanData) => void;
}

const ClanSearchInput = ({ searchType, showIcon, onSubmit, onHistoryItemClick }: IClanSearchInput) => {
  const dispatch = useAppDispatch();

  const { autocomplete, term } = useAppSelector(stateSelector, shallowEqual);
  const currentAccount = useAppSelector(currentAccountSelector);

  const [isFocused, setIsFocused] = useState(false);
  const [termState, setTermState] = useState(term);
  const [isAutocompleteVisible, setIsAutocompleteVisible] = useState(false);

  const autocompleteAbortController = useRef<AbortController>(new AbortController());

  const inputRef = useRef<Nullable<HTMLInputElement>>(null);
  const autocompleteRef = useRef(null);

  useMount(() => {
    return () => {
      autocompleteAbortController.current?.abort();
    };
  });

  useClickOutside([inputRef, autocompleteRef], () => {
    setIsAutocompleteVisible(false);
  });

  useEffect(() => {
    setTermState(term);
  }, [term]);

  const onClearClick = useCallback(() => {
    void playButtonClickSound();

    setTermState('');
    setIsFocused(true);
    setIsAutocompleteVisible(true);

    inputRef.current?.focus();

    dispatch(autocompleteActions.clearAutocompleteState());
    dispatch(actionsSearch.clearSearchState());
  }, [dispatch]);

  const onFocus = () => {
    if (isFocused && isAutocompleteVisible) {
      return;
    }

    dwhExport.push(DWH_EVENTS.SEARCH.INPUT_FOCUS);

    setIsFocused(true);
    setIsAutocompleteVisible(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnoughSymbols = termState && termState.length >= settings.search.minChars;

    if (event.code === 'Enter' && isEnoughSymbols) {
      void playButtonClickSound();
      setIsAutocompleteVisible(false);
      handleSubmit();
    }
  };

  const fetchData = useCallback(
    (term: string) => {
      if (autocomplete.isFetching) {
        autocompleteAbortController.current?.abort();
        autocompleteAbortController.current = new AbortController();
      }

      void dispatch(
        fetchAutompleteResultThunk({
          term,
          type: searchType,
          abortSignal: autocompleteAbortController.current?.signal,
        }),
      );
    },
    [autocomplete.isFetching, dispatch, searchType],
  );

  const onChangePurified = useCallback(
    (term: string) => {
      if (term) {
        void fetchData(term);
      }
    },
    [fetchData],
  );

  const onChangeDebounced = useMemo(() => debounce(onChangePurified, DEBOUNCE_INTERVAL), [onChangePurified]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();

      const trimmedValue = event.target?.value?.trim() || '';
      const termValue = trimmedValue ? event.target.value : trimmedValue;

      if (!trimmedValue) {
        onClearClick();
      } else {
        setTermState(termValue);
      }

      void playInputSound();
      onChangeDebounced(termValue);
    },
    [onChangeDebounced, onClearClick],
  );

  const handleHistoryItemClick = (item: IClanData) => {
    onHistoryItemClick(item);
    setIsAutocompleteVisible(false);
  };

  const handleAutocompleteItemClick = (item: IClanData) => {
    dispatch(actionsSearch.appendTermToHistory(item, currentAccount.id || 0));
  };

  const handleSubmit = () => {
    autocompleteAbortController.current?.abort();
    inputRef.current?.blur();
    setIsAutocompleteVisible(false);

    const isNotEnoughSymbols = (inputRef.current?.value.length || 0) < settings.search.minChars;

    if (termState && !isNotEnoughSymbols) {
      onSubmit?.();
      dispatch(actionsSearch.changeTerm(termState));
      void dispatch(fetchSearchResultThunk({ type: searchType }));
    }
  };

  const onEnterClick = () => {
    void playButtonClickSound();
    handleSubmit();
  };

  const renderAutocomplete = () => {
    if (!isAutocompleteVisible) {
      return null;
    }

    return (
      <div ref={autocompleteRef}>
        <ClanSearchAutocomplete
          isHistoryHidden={searchType === 'clansRating'}
          term={termState || ''}
          onHistoryItemClick={handleHistoryItemClick}
          onItemClick={handleAutocompleteItemClick}
          onSubmit={handleSubmit}
        />
      </div>
    );
  };

  const renderControl = () => {
    if (!termState) {
      return null;
    }

    return (
      <div className={styles.inputButtonsWrap}>
        {termState.length >= 2 ? <div className={styles.button} onClick={onEnterClick} /> : null}
        <div className={styles.clear} onClick={onClearClick} />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <input
        className={cx(styles.input, {
          [styles.isIconVisible]: showIcon,
        })}
        maxLength={settings.search.maxChars}
        placeholder={isFocused ? '' : t('Введите тег или название клана')}
        ref={inputRef}
        spellCheck={false}
        type="text"
        value={termState}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
      />
      {showIcon && <span className={styles.icon} />}
      {renderAutocomplete()}
      {renderControl()}
    </div>
  );
};

export default memo(ClanSearchInput);
